/**
 * Copyright(c) 2024 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module SEO
 *
 * @author Anoop SS
 */

import { Block } from "../common/lib/types";
import { saleorBlockType } from "./constants";
import { decodeHtmlEntities, extractTextFromHtml } from "../utils/textProcessing";

/**
 * This function used to parse the html content and splice it with provided breakpoint index.
 * @param html Description text
 * @param breakPoint limiting index.
 * @returns []
 */
function htmlDataParserUtils(html: string, breakPoint: number) {
  if (!html) return ["", "", ""];
  
  // Use the centralized HTML entity decoding
  const escapedHtml = decodeHtmlEntities(html);
    
  const textContentRegex = />(.*?)</g;
  let match;
  let counter = 0;
  if (escapedHtml && escapedHtml?.length > breakPoint) {
    while ((match = textContentRegex?.exec(escapedHtml)) !== null) {
      const length = match[1]?.length;
      if (length > 0) {
        const startIndx = match?.index;
        if (counter + length >= breakPoint) {
          const breakIndex = startIndx + (breakPoint - counter) + 1;
          return [
            escapedHtml?.substring(0, breakIndex)?.replace(/<\/?p>/g, ""),
            escapedHtml?.substring(breakIndex)?.replace(/<\/?p>/g, ""),
            escapedHtml.replace(/<\/?p>/g, ""),
          ];
        }
        counter += length;
      }
    }
  }
  return [escapedHtml?.replace(/<\/?p>/g, ""), "", escapedHtml?.replace(/<\/?p>/g, "")];
}

/**
 * This function used to parse the html content and splice it with provided breakpoint index.
 * @param html Description text
 * @param mobileBreakPoint limiting index for mobile view.
 * @param desktopBreakPoint limiting index for desktop view.
 * @returns []
 */
// export function htmlDataParserUtilsV2(html: string, mobileBreakPoint: number, desktopBreakPoint: number) {
//   if (!html) return ["", "", ""];

//   // Replace &amp; and remove <p> tags once at the beginning for efficiency
//   const escapedHtml = html.replaceAll("&amp;", "&").replace(/<\/?p>/g, "");
//   const textContentRegex = />(.*?)</g;
//   let match;
//   let counter = 0;
//   let minViewForMobile = "";
//   let minViewForDesktop = "";

//   // Only process further if the length is greater than the smaller breakpoint
//   const needsProcessing = escapedHtml.length > Math.min(mobileBreakPoint, desktopBreakPoint);

//   if (needsProcessing) {
//     while ((match = textContentRegex.exec(escapedHtml)) !== null) {
//       const textSegment = match[1];
//       const length = textSegment.length;

//       if (length > 0) {
//         const startIndx = match.index;

//         // Calculate mobile view if the breakpoint is reached
//         if (!minViewForMobile && counter + length >= mobileBreakPoint) {
//           const mobileBreakIndex = startIndx + (mobileBreakPoint - counter) + 1;
//           minViewForMobile = escapedHtml.substring(0, mobileBreakIndex);
//         }

//         // Calculate desktop view if the breakpoint is reached
//         if (!minViewForDesktop && counter + length >= desktopBreakPoint) {
//           const desktopBreakIndex = startIndx + (desktopBreakPoint - counter) + 1;
//           minViewForDesktop = escapedHtml?.substring(0, desktopBreakIndex);
//         }

//         // Exit the loop early if both mobile and desktop breakpoints are calculated
//         if (minViewForMobile && minViewForDesktop) {
//           break;
//         }

//         counter += length;
//       }
//     }
//   }

//   // Fallback to full view if breakpoints aren't reached
//   minViewForMobile = minViewForMobile || escapedHtml;
//   minViewForDesktop = minViewForDesktop || escapedHtml;

//   return [minViewForMobile, minViewForDesktop, escapedHtml];
// }

export function htmlDataParserUtilsV2(html: string, mobileBreakPoint: number, desktopBreakPoint: number) {
  if (!html) return ["", "", ""];

  // Server-side fallback for environments without DOMParser
  if (typeof window === "undefined" && typeof DOMParser === "undefined") {
    // Use centralized HTML entity decoding
    const cleanHtml = decodeHtmlEntities(html);
    
    // Extract text content for character counting
    const textContent = extractTextFromHtml(cleanHtml);
    
    // Simple truncation for server-side
    const mobileView = textContent.length <= mobileBreakPoint ? cleanHtml : 
      cleanHtml.substring(0, mobileBreakPoint) + "...";
    const desktopView = textContent.length <= desktopBreakPoint ? cleanHtml : 
      cleanHtml.substring(0, desktopBreakPoint) + "...";
    
    return [mobileView, desktopView, cleanHtml];
  }
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const getTruncatedHTML = (maxChars: number): string => {
      const clone = doc.body.cloneNode(true) as HTMLElement;
      const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
      let charCount = 0;
      let node: Node | null;
  
      while ((node = walker.nextNode())) {
        const text = node.textContent || "";
        const remaining = maxChars - charCount;
  
        if (text.length + charCount <= maxChars) {
          charCount += text.length;
        } else {
          // Preserve word boundaries when truncating
          const truncatedText = text.slice(0, remaining);
          const lastSpaceIndex = truncatedText.lastIndexOf(" ");
          
          if (lastSpaceIndex > 0 && remaining - lastSpaceIndex < 10) {
            // If we're close to a word boundary, truncate at the word
            node.textContent = truncatedText.slice(0, lastSpaceIndex);
          } else {
            node.textContent = truncatedText;
          }
          
          // Remove all following siblings/descendants after truncation
          while ((node = walker.nextNode())) {
            node.textContent = "";
          }
          break;
        }
      }
  
      const inner = clone.innerHTML
        .replace(/<\/?(p|div|h[1-6]|section|article|ul|li)>/gi, ""); // Remove block tags
  
      return `<span>${inner}</span>`;
    };
    
    // Extract the raw text content without tags for accurate counting using centralized function
    const rawText = extractTextFromHtml(html);
    
    // Generate mobile and desktop views based on breakpoints
    const mobileView = rawText.length <= mobileBreakPoint ? html : getTruncatedHTML(mobileBreakPoint);
    const desktopView = rawText.length <= desktopBreakPoint ? html : getTruncatedHTML(desktopBreakPoint);
  
    return [mobileView, desktopView, html];
  } catch (error) {
    console.warn("Error parsing HTML content:", error);
    // Fallback to original content if parsing fails
    return [html, html, html];
  }
}


/**
 * This function used to fetch get the inner text content from html content
 * @param html html content
 * @returns string
 */
function getHtmlInnerText(html: string) {
  if (!html) return "";
  
  // Use centralized text extraction
  return extractTextFromHtml(html);
}

function convertToHTML(blocks: Block[]): string {
  return blocks
    ?.map((block: Block) => {
      switch (block.type) {
        case saleorBlockType.PARAGRAPH:
          return `<p>${block.data.text}</p>`;
        case saleorBlockType.HEADER:
          return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
        case saleorBlockType.LIST: {
          const listItems: string = block.data.items.map((item: string) => `<li>${item}</li>`).join("");
          const listType: "ol" | "ul" = block.data.style === "ordered" ? "ol" : "ul";
          return `<${listType}>${listItems}</${listType}>`;
        }
        case saleorBlockType.QUOTE:
          return `<blockquote>${block.data.text}<br/><cite>${block.data.caption}</cite></blockquote>`;
        default:
          // Handle other block types if needed
          return "";
      }
    })
    .join("");
}

export { htmlDataParserUtils, getHtmlInnerText, convertToHTML };
